import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Col, Row } from 'reactstrap';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz, colorSchemeDark, colorSchemeLight } from 'ag-grid-community';
import config from '../../../../../config';
import ChartBuilder from '../../../../../shared/components/chart/ChartBuilder';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import DateRangePicker from '../../../../../shared/components/widgets/DateRangePicker';
import { currencyFormatter, currencyFormatterDirect, formatNumberDefaultToZero, numberTwoPlaces } from '../../../../../shared/components/table/functions';
import { getChartTheme } from '../../../../../shared/components/chart/chartThemeHelper';

export default function OnaDailyPerformanceLayout() {
  const gridRef = useRef();
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [apiLoad, setApiLoad] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(9, 'days')); // moment('20231005', 'YYYYMMDD')
  const [endDate, setEndDate] = useState(moment().subtract(2, 'days'));

  const [apiData, setApiData] = useState([]);

  const panelRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 320 });

  useLayoutEffect(() => {
    if (!panelRef.current) {
      return () => {};
    }
    const el = panelRef.current;

    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      // constrain min height so the chart always has space
      setSize({ w: Math.round(cr.width), h: Math.max(260, Math.round(cr.height)) });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setApiLoad(true);
    // setGraphData([{}]);
    setApiData([{}]);

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const performnceUrl = `${config.serverUrl}/dsp/ona/daily/performance/report?accountId=${sellerSelect.value}&startDate=${startDate.format('YYYYMMDD')}&endDate=${endDate.format('YYYYMMDD')}`;
    fetch(performnceUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then((data) => {
        setApiData(data);
        setApiLoad(false);
      }).catch(() => {
        setApiLoad(false);
      });
  }, [sellerSelect.value, startDate, endDate]);

  function buildMasters(rows) {
    const toNum = (v) => (v == null ? 0 : Number(v) || 0);
    const safeDiv = (n, d) => (d ? n / d : null);

    const source = Array.isArray(rows) ? rows : [];
    const byKey = new Map();

    for (let i = 0; i < source.length; i += 1) {
      const r = source[i] || {};
      const key = `${String(r.mlp_id)}||${String(r.mlp_type)}||${String(r.mlp_name)}||${String(r.mlp_url)}`;

      let group = byKey.get(key);
      if (!group) {
        group = {
          mlp_id: r.mlp_id || null,
          mlp_type: r.mlp_type || null,
          mlp_name: r.mlp_name || null,
          mlp_url: r.mlp_url || null,

          total_ona_spend: 0,
          total_ona_impressions: 0,
          total_ona_detail_page_views: 0,
          total_ona_atc: 0,
          total_ona_sales: 0,
          total_ona_units: 0,
          total_ona_reach: 0,
          total_ona_household_reach: 0,
          total_ona_purchases: 0,

          ona_cpm: null,
          ona_cpdpv: null,
          ona_cac: null,
          ona_roas: null,

          daily: [],
        };
        byKey.set(key, group);
      }

      group.total_ona_spend += toNum(r.total_ona_spend);
      group.total_ona_impressions += toNum(r.total_ona_impressions);
      group.total_ona_detail_page_views += toNum(r.total_ona_detail_page_views);
      group.total_ona_atc += toNum(r.total_ona_atc);
      group.total_ona_sales += toNum(r.total_ona_sales);
      group.total_ona_units += toNum(r.total_ona_units);
      group.total_ona_reach += toNum(r.total_ona_reach);
      group.total_ona_household_reach += toNum(r.total_ona_household_reach);
      group.total_ona_purchases += toNum(r.total_ona_purchases);

      group.daily.push(r);
    }

    const masters = Array.from(byKey.values());

    for (let i = 0; i < masters.length; i += 1) {
      const g = masters[i];

      g.ona_cpm = safeDiv(g.total_ona_spend * 1000, g.total_ona_impressions);
      g.ona_cpdpv = safeDiv(g.total_ona_spend, g.total_ona_detail_page_views);
      g.ona_cac = safeDiv(g.total_ona_spend, g.total_ona_purchases); // or use conversions if preferred
      g.ona_roas = safeDiv(g.total_ona_sales, g.total_ona_spend);

      g.daily.sort((a, b) => {
        const da = a && a.date ? String(a.date) : '';
        const db = b && b.date ? String(b.date) : '';
        if (da < db) return -1;
        if (da > db) return 1;
        return 0;
      });
    }

    return masters;
  }

  const masters = useMemo(
    () => buildMasters((apiData && apiData.results) || []),
    [apiData],
  );

  const graphData = useMemo(() => {
    if (!Array.isArray(masters) || masters.length === 0) return [];

    // dateKey -> { spend, sales }
    const index = Object.create(null);

    for (let i = 0; i < masters.length; i += 1) {
      const daily = Array.isArray(masters[i]?.daily) ? masters[i].daily : [];
      for (let j = 0; j < daily.length; j += 1) {
        const row = daily[j] || {};
        const key = row.date ? String(row.date).slice(0, 10) : '';
        if (key) {
          const bucket = index[key] || (index[key] = {
            dateKey: key,
            spend: 0,
            sales: 0,
          }); // trailing comma OK
          bucket.spend += Number(row.total_ona_spend) || 0;
          bucket.sales += Number(row.total_ona_sales) || 0;
        }
      }
    }

    const keys = Object.keys(index).sort(); // ascending date
    const out = new Array(keys.length);
    for (let k = 0; k < keys.length; k += 1) {
      const dk = keys[k];
      const { spend, sales } = index[dk]; // prefer-destructuring
      out[k] = {
        date: dk, // AG Charts time-axis friendly
        ona_spend: spend,
        ona_sales: sales,
        ona_roas: spend ? sales / spend : null,
      }; // trailing comma OK
    }

    return out;
  }, [masters]);

  const totals = useMemo(() => {
    let spend = 0;
    let sales = 0;
    (Array.isArray(masters) ? masters : []).forEach((m) => {
      spend += Number(m.total_ona_spend) || 0;
      sales += Number(m.total_ona_sales) || 0;
    });
    return { spend, sales, roas: spend ? (sales / spend) : null };
  }, [masters]);

  function aggWalk(params) {
    const data = {
      cpm: 0,
      cpdpv: 0,
      cac: 0,
      roas: 0,
    }


    let spend = 0;
    let imps = 0;
    let dpv = 0;
    // let units = 0;
    let purchases = 0;
    let sales = 0;

    const walk = (node) => {
      if (node.group) {
        (node.childrenAfterFilter || node.childrenAfterGroup || node.childrenMapped)?.forEach(walk);
      } else {
        const d = node.data || {};
        spend += Number(d.total_ona_spend) || 0;
        imps += Number(d.total_ona_impressions) || 0;
        dpv += Number(d.total_ona_detail_page_views) || 0;
        // units += Number(d.total_ona_units) || 0;
        purchases += Number(d.total_ona_purchases) || 0;
        sales += Number(d.total_ona_sales) || 0;
      }
    };

    walk(params.rowNode);
    data.cpm = imps ? spend / (imps / 1000) : null;
    data.cpdpv = dpv ? spend / dpv : null;
    data.cac = purchases ? spend / purchases : null;
    data.roas = spend ? sales / spend : null;
    return data;
  }

  const masterCols = useMemo(() => [
    { field: 'mlp_id', headerName: 'Master LP ID', cellRenderer: 'agGroupCellRenderer', pinned: 'left' },
    { field: 'mlp_type', headerName: 'Master LP Type', pinned: 'left' },
    { field: 'mlp_name', headerName: 'Master LP Name', pinned: 'left' },
    { field: 'mlp_url', headerName: 'Master LP URL', pinned: 'left' },
    { field: 'total_ona_spend', headerName: 'Total ONA Spend', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'total_ona_impressions', headerName: 'Total ONA Impressions', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'ona_cpm', headerName: 'ONA CPM', valueFormatter: currencyFormatter, aggFunc: (params) => aggWalk(params).cpm },
    { field: 'ona_cpdpv', headerName: 'ONA Cost per DPV', valueFormatter: currencyFormatter, aggFunc: (params) => aggWalk(params).cpdpv },
    { field: 'total_ona_detail_page_views', headerName: 'Total ONA DPV', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'total_ona_atc', headerName: 'Total ONA ATC', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'total_ona_sales', headerName: 'Total ONA Sales', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'total_ona_units', headerName: 'Total ONA Units', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'ona_cac', headerName: 'ONA CAC', valueFormatter: currencyFormatter, aggFunc: (params) => aggWalk(params).cac },
    { field: 'ona_roas', headerName: 'ONA ROAS', valueFormatter: numberTwoPlaces, aggFunc: (params) => aggWalk(params).roas },
    { field: 'total_ona_reach', headerName: 'Total ONA User Reach', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    // { field: 'total_ona_household_reach', headerName: 'total_ona_household_reach', valueFormatter: formatNumberDefaultToZero },
  ], []);


  const detailCellRendererParams = useMemo(() => ({
    detailGridOptions: {
      columnDefs: [
        { field: 'date', headerName: 'Date' },
        { field: 'total_ona_spend', headerName: 'Total ONA Spend', valueFormatter: currencyFormatter },
        { field: 'total_ona_impressions', headerName: 'Total ONA Impressions', valueFormatter: formatNumberDefaultToZero },
        { field: 'ona_cpm', headerName: 'ONA CPM', valueFormatter: currencyFormatter },
        { field: 'ona_cpdpv', headerName: 'ONA Cost per DPV', valueFormatter: currencyFormatter },
        { field: 'total_ona_detail_page_views', headerName: 'Total ONA DPV', valueFormatter: formatNumberDefaultToZero },
        { field: 'total_ona_atc', headerName: 'Total ONA ATC', valueFormatter: formatNumberDefaultToZero },
        { field: 'total_ona_sales', headerName: 'Total ONA Sales', valueFormatter: currencyFormatter },
        { field: 'total_ona_units', headerName: 'Total ONA Units', valueFormatter: formatNumberDefaultToZero },
        { field: 'ona_cac', headerName: 'ONA CAC', valueFormatter: currencyFormatter },
        { field: 'ona_roas', headerName: 'ONA ROAS', valueFormatter: numberTwoPlaces },
        { field: 'total_ona_reach', headerName: 'Total ONA User Reach', valueFormatter: formatNumberDefaultToZero },
        // { field: 'total_ona_household_reach', headerName: 'total_ona_household_reach', valueFormatter: formatNumberDefaultToZero },
      ],
      defaultColDef: { flex: 1, sortable: true, resizable: true },
      rowSelection: 'multiple',
    },
    getDetailRowData: (params) => {
      params.successCallback(params.data.daily || []);
    },
  }), []);

  function dateChange(start, end) {
    setStartDate(moment(start));
    setEndDate(moment(end));
  }

  const chartOptions = {
    theme: getChartTheme(theme),
    data: graphData,
    series: [
      {
        type: 'line',
        xKey: 'date',
        yKey: 'ona_sales',
        yName: 'ONA Sales',
        tooltip: {
          renderer: ({ datum }) => ({
            title: `Sales ${datum.date}`,
            content: currencyFormatter({ value: datum.ona_sales }),
          }),
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'ona_spend',
        yName: 'ONA Spend',
        tooltip: {
          renderer: ({ datum }) => ({
            title: `Spend ${datum.date}`,
            content: currencyFormatter({ value: datum.ona_spend }),
          }),
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'ona_roas',
        yName: 'ONA ROAS',
        tooltip: {
          renderer: ({ datum }) => ({
            title: `ROAS ${datum.date}`,
            content: numberTwoPlaces({ value: datum.ona_roas }),
          }),
        },
      },
    ],
    axes: [
      { type: 'category', position: 'bottom', title: { text: 'Date' } },
      {
        type: 'number',
        position: 'left',
        keys: ['ona_sales', 'ona_spend'],
        title: {
          text: 'Dollars',
        },
      },
      {
        type: 'number',
        position: 'right',
        keys: ['ona_roas'],
        title: {
          text: 'ROAS',
        },
      },
    ],
    title: {
      text: 'DSP ONA Daily Performance',
    },
    // subtitle: {
    //   text: '2024',
    // },
    width: size.w,
    height: size.h,
    autoSize: false,
  };

  // Table Settings
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    flex: 1,
  }), []);

  const onGridReady = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }, []);

  const isDarkMode = theme.className && !theme.className.endsWith('light');
  const useTheme = isDarkMode
    ? themeQuartz.withPart(colorSchemeDark)
    : themeQuartz.withPart(colorSchemeLight);

  const onBtnExport = () => {
    const fileName = `${sellerSelect.value}-DSP-ONA-Daily-Performance-${moment(startDate).format('YYYYMMDD')}-${moment(endDate).format('YYYYMMDD')}.csv`;
    gridRef.current.api.exportDataAsCsv({ fileName });
  };

  return (
    <Container className="dashboard">
      <Row>
        <Col md={12}>
          <CardPanel
            showButtons={false}
            parentRefresh={apiLoad}
          >
            <div ref={panelRef} className="resizable-panel">
              <div className={useTheme} style={{ height: '100%', width: '100%' }}>
                <ChartBuilder chartOptions={chartOptions} />
              </div>
            </div>
          </CardPanel>
        </Col>
      </Row>
      {/* Totals cards */}
      <Row>
        <Col md={12}>
          <CardPanel showButtons={false} parentRefresh={apiLoad}>
            <Row className="text-center">
              <Col xs={12} md={4} className="mb-3 mb-md-0">
                <div>
                  <div style={{ color: isDarkMode ? '#999' : '#6c757d' }}>Total Sales</div>
                  <div className="h3 m-0" style={{ color: isDarkMode ? '#fff' : '#212529' }}>{currencyFormatterDirect(totals.sales)}</div>
                </div>
              </Col>
              <Col xs={12} md={4} className="mb-3 mb-md-0">
                <div>
                  <div style={{ color: isDarkMode ? '#999' : '#6c757d' }}>Total Spend</div>
                  <div className="h3 m-0" style={{ color: isDarkMode ? '#fff' : '#212529' }}>{currencyFormatterDirect(totals.spend)}</div>
                </div>
              </Col>
              <Col xs={12} md={4}>
                <div>
                  <div style={{ color: isDarkMode ? '#999' : '#6c757d' }}>ROAS</div>
                  <div className="h3 m-0" style={{ color: isDarkMode ? '#fff' : '#212529' }}>{numberTwoPlaces({ value: totals.roas })}</div>
                </div>
              </Col>
            </Row>
          </CardPanel>
        </Col>
      </Row>
      <Row>
        <Col>
          <CardPanel
            lg={12}
            md={12}
            title=""
            subhead=""
            showButtons={false}
            parentRefresh={apiLoad}
          >
            <Row xs={1} md={2} className="align-items-center g-3 p-3 p-md-0">
              {/* left col: center on mobile, normal on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-start text-center text-md-start">
                <DateRangePicker
                  startDate={startDate.toDate()}
                  endDate={endDate.toDate()}
                  onDateChange={dateChange}
                />
              </Col>

              {/* right col: add top padding only when stacked, center on mobile, right on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-end pt-3 pt-md-0">
                <button
                  className="btn btn-outline-primary"
                  style={{ width: 200 }}
                  onClick={onBtnExport}
                  type="button"
                >
                  Export
                </button>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className={useTheme} style={{ height: '50vh', width: '100%' }}>
                  <AgGridReact
                    theme={useTheme}
                    ref={gridRef}
                    rowData={masters}
                    columnDefs={masterCols}
                    defaultColDef={defaultColDef}
                    masterDetail
                    isRowMaster={(dataItem) => Array.isArray(dataItem?.daily) && dataItem.daily.length > 0}
                    detailCellRendererParams={detailCellRendererParams}
                    detailRowAutoHeight
                    groupSuppressAutoColumn={false}
                    animateRows
                    grandTotalRow="pinnedBottom"
                    onFirstDataRendered={(p) => p.api.forEachNode(n => n.setExpanded(false))}
                    onGridReady={onGridReady}
                  />
                </div>
              </Col>
            </Row>
          </CardPanel>
        </Col>
      </Row>
    </Container>
  );
}
